import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  TextField,
  Button,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Modal,
  Box,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  Divider,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../api';
import { useNavigate } from 'react-router-dom';
import Layout from '../../Layout';

// Icons
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import GavelIcon from '@mui/icons-material/Gavel';
import EventIcon from '@mui/icons-material/Event';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SendIcon from '@mui/icons-material/Send';
import LockClockIcon from '@mui/icons-material/LockClock';

const StyledTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(2, 0),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    background: theme.palette.background.paper,
  },
}));

// Dropdown menu sizing for a nicer feel
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
      borderRadius: 12,
    },
    elevation: 6,
  },
};

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  padding: theme.spacing(5),
  background: 'rgba(255,255,255,0.72)',
  border: '1px solid rgba(255,255,255,0.35)',
  boxShadow: '0 20px 50px rgba(16, 24, 40, 0.1)',
  backdropFilter: 'blur(10px)',
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: 1,
  background: 'linear-gradient(90deg, #2e7d32 0%, #00bfa6 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const Pill = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 999,
  background: 'rgba(46,125,50,0.1)',
  color: '#1b5e20',
  fontWeight: 600,
  fontSize: 13,
}));

const DetailedReportPage = () => {
  const [selections, setSelections] = useState({
    company: '',
    location: [],
    lawArea: [],
    startDate: '',
    endDate: '',
  });
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [lawareas, setLawAreas] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data } = await api.get('detail/userinfo?view=meta');
        const companyNames = Array.isArray(data.companyDetails.company_name)
          ? data.companyDetails.company_name
          : data.companyDetails.company_name
            ? [data.companyDetails.company_name]
            : [];
        setCompanies(companyNames);
        setLocations(data.companyDetails.locations || []);
      } catch (error) {
        if (error?.response?.status === 401) {
          setSessionModalOpen(true);
        }
        console.error('Error fetching data:', error);
      }
    };

    const fetchLawAreas = async () => {
      try {
        const response = await api.get('tracker/law-area');
        if (response.data && response.data.lawareas) {
          const lawAreaArray = response.data.lawareas.map((lawArea) => lawArea.law_area);
          setLawAreas(lawAreaArray);

        } else {
          console.error('Unexpected response structure:', response.data);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setSessionModalOpen(true);
        }
        console.error('Error fetching data:', error);
      }
    };
    fetchLawAreas();
    fetchCompanies();
  }, []);

  const handleSessionModalClose = () => {
    setSessionModalOpen(false);
    navigate('/login');
  };

  const handleSubmit = async () => {
    const { company, location, lawArea, startDate, endDate } = selections;

    if (new Date(startDate) > new Date(endDate)) {
      toast.error("Start Date can't be bigger than End Date.");
      return;
    }

    if (!company || !location.length || !lawArea.length || !startDate || !endDate) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      const response = await api.post('reports/detailed-report', selections);
      if (response.status === 200) {
        toast.success('Report generated successfully!');
        navigate('/reports/detailed-report-results', {
          state: { reportData: response.data, Company: company, startDate, enddate: endDate, Location: location },
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report. Please try again.');
    }
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;

    if (field === 'location' || field === 'lawArea') {
      if (Array.isArray(value)) {
        if (value.includes('ALL')) {
          setSelections((prev) => ({
            ...prev,
            [field]: ['ALL'],
          }));
        } else {
          setSelections((prev) => ({
            ...prev,
            [field]: value.filter((v) => v !== 'ALL'),
          }));
        }
      }
    } else {
      setSelections((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // --- UI helpers (purely visual) ---
  const renderChips = (selected) => {
    if (!selected?.length) return <span style={{ color: '#9e9e9e' }}>Nothing selected</span>;
    if (selected.includes('ALL')) return <Chip label="All" size="small" />;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((value) => (
          <Chip key={value} label={value} size="small" />
        ))}
      </Box>
    );
  };

  return (
    <Layout title="Detailed Report">
      {/* Session modal with a cleaner look */}
      <Modal
        open={sessionModalOpen}
        onClose={handleSessionModalClose}
        aria-labelledby="session-expired-modal-title"
        aria-describedby="session-expired-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 420,
            bgcolor: 'background.paper',
            borderRadius: 4,
            boxShadow: 24,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, #ff5f6d 0%, #ffc371 100%)',
              color: '#fff',
            }}
          >
            <LockClockIcon />
          </Box>
          <Typography
            id="session-expired-modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 1.5, fontWeight: 'bold' }}
          >
            Session Expired
          </Typography>
          <Typography id="session-expired-modal-description" sx={{ mb: 3, color: 'text.secondary' }}>
            Your session has expired. Please log in again to continue.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSessionModalClose}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #2e7d32, #00bfa6)',
              boxShadow: '0 10px 18px rgba(0,191,166,0.25)',
              '&:hover': {
                background: 'linear-gradient(90deg, #1b5e20, #00a58f)',
              },
            }}
          >
            Go to Login
          </Button>
        </Box>
      </Modal>

      {/* Background wrapper */}
      <Box
        sx={{
          minHeight: '100vh',
          pt: { xs: 4, sm: 6 },
          pb: { xs: 6, sm: 8 },
          background:
            'radial-gradient(1200px 400px at -10% -10%, rgba(0,191,166,0.12), transparent 50%), radial-gradient(1000px 500px at 110% 10%, rgba(46,125,50,0.12), transparent 60%), linear-gradient(180deg, #f7fbf9 0%, #f3f7f4 100%)',
        }}
      >
        <Container maxWidth="md">
          <GlassPaper elevation={0}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Pill>
                <FilterAltIcon fontSize="small" />
                Filters
              </Pill>
            </Box>

            <Heading variant="h4" align="center" sx={{ mb: 1 }}>
              Select Filters
            </Heading>
            <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
              Choose a company, location(s), law area(s), and a date range to generate your report.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {/* Company */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={selections.company}
                    onChange={handleInputChange('company')}
                    label="Company"
                    displayEmpty
                    input={<OutlinedInput label="Company" />}
                    MenuProps={MenuProps}
                    sx={{
                      borderRadius: 2,
                      '& .MuiSelect-select': { py: 1.5 },
                    }}
                  >
                    {companies.map((company, index) => (
                      <MenuItem key={index} value={company}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" />
                          <span>{company}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Pick the company for which you want to view compliance details.
                  </Typography>
                </FormControl>
              </Grid>

              {/* Location */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    multiple
                    label="Location"
                    value={selections.location}
                    onChange={handleInputChange('location')}
                    input={<OutlinedInput label="Location" />}
                    renderValue={renderChips}
                    MenuProps={MenuProps}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="ALL">
                      <Checkbox checked={selections.location.includes('ALL')} />
                      <ListItemText primary="All" />
                    </MenuItem>

                    {selections.company !== '' &&
                      locations.map((locationGroup, groupIndex) =>
                        locationGroup.map((location, index) => {
                          const selected = selections.location.includes(location);
                          return (
                            <MenuItem key={`${groupIndex}-${index}`} value={location}>
                              <Checkbox checked={selected} />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PlaceIcon fontSize="small" />
                                    <span>{location}</span>
                                  </Box>
                                }
                              />
                            </MenuItem>
                          );
                        })
                      )}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    You can select multiple locations or choose “All”.
                  </Typography>
                </FormControl>
              </Grid>

              {/* Law Areas */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Law Areas</InputLabel>
                  <Select
                    multiple
                    label="Law Areas"
                    value={selections.lawArea}
                    onChange={handleInputChange('lawArea')}
                    input={<OutlinedInput label="Law Areas" />}
                    renderValue={renderChips}
                    MenuProps={MenuProps}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="ALL">
                      <Checkbox checked={selections.lawArea.includes('ALL')} />
                      <ListItemText primary="All" />
                    </MenuItem>

                    {lawareas.map((lawArea, index) => {
                      const selected = selections.lawArea.includes(lawArea);
                      return (
                        <MenuItem key={index} value={lawArea}>
                          <Checkbox checked={selected} />
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GavelIcon fontSize="small" />
                                <span>{lawArea}</span>
                              </Box>
                            }
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Narrow down by one or more legal areas.
                  </Typography>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={selections.startDate}
                  onChange={handleInputChange('startDate')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  label="End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={selections.endDate}
                  onChange={handleInputChange('endDate')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventBusyIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Submit */}
              <Grid item xs={12}>
                <Tooltip title="Generate a detailed report with your selected filters">
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    endIcon={<SendIcon />}
                    sx={{
                      py: 1.6,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 800,
                      letterSpacing: 0.3,
                      fontSize: '1.05rem',
                      color: '#fff',
                      background: 'linear-gradient(90deg, #2e7d32 0%, #00bfa6 100%)',
                      boxShadow: '0 14px 28px rgba(46,125,50,0.25)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #1b5e20 0%, #00a58f 100%)',
                        boxShadow: '0 16px 32px rgba(0,165,143,0.3)',
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </GlassPaper>
        </Container>
      </Box>
    </Layout>
  );
};

export default DetailedReportPage;
